/**
 * Sesión de operador (docs/FASE-1.md §6 "Autenticación"). La consola NO usa OAuth/PKCE: habla
 * directo con `POST /api/v1/operators/login|refresh|logout`. El access token vive SOLO en
 * memoria (el estado de este módulo); el refresh token se persiste en `sessionStorage` bajo la
 * clave `cnt.console.refresh` para sobrevivir a una recarga de página sin pedir contraseña de
 * nuevo, pero nunca en `localStorage` ni en una cookie escrita desde JS.
 *
 * `OperatorLoginResponseSchema`/el shape de `{ refresh_token }` que documenta docs/FASE-1.md §5
 * todavía no existen en `@continnua/contracts`: los añade en paralelo el agente `infra-idp`, único
 * dueño de `packages/contracts/src/dto/operators.ts`. Para no bloquearse esperando ese trabajo, se
 * define aquí el mismo esquema exacto que documenta la especificación (reutilizando `OperatorSchema`
 * ya publicado para el campo anidado `operator`). Cuando ese DTO llegue al paquete compartido,
 * `OperatorLoginResponseSchema` de aquí debe sustituirse por la importación de `@continnua/contracts`.
 */

import { OperatorSchema, type Operator } from "@continnua/contracts";
import {
  createContext,
  createElement,
  useContext,
  useEffect,
  useSyncExternalStore,
  type ReactElement,
  type ReactNode,
} from "react";
import { z } from "zod";

import { ConsoleApiError, rawRequest, type ApiRequestInit } from "./http";

const REFRESH_STORAGE_KEY = "cnt.console.refresh";
/** Refresco proactivo 60 s antes de que expire el access token (docs/FASE-1.md §6). */
const PROACTIVE_REFRESH_SKEW_MS = 60_000;

/** Ver nota de cabecera: sustituto temporal de `OperatorLoginResponseSchema` de `@continnua/contracts`. */
const OperatorLoginResponseSchema = z.object({
  access_token: z.string(),
  token_type: z.literal("Bearer"),
  expires_in: z.number().int().positive(),
  refresh_token: z.string(),
  operator: OperatorSchema,
});
type OperatorLoginResponse = z.infer<typeof OperatorLoginResponseSchema>;

export interface OperatorLoginInput {
  email: string;
  password: string;
  /** Requerido cuando el operador ya activó TOTP; ver `docs/FASE-0.md §5`. */
  totp?: string;
}

export type SessionStatus = "anonymous" | "restoring" | "authenticating" | "authenticated";

interface SessionState {
  status: SessionStatus;
  accessToken: string | null;
  accessTokenExpiresAt: number | null;
  operator: Operator | null;
}

export interface SessionSnapshot {
  status: SessionStatus;
  operator: Operator | null;
}

let state: SessionState = { status: "anonymous", accessToken: null, accessTokenExpiresAt: null, operator: null };
const listeners = new Set<() => void>();
/** Instantánea cacheada; su referencia solo cambia cuando cambia `state` (ver `getSnapshot`). */
let snapshot: SessionSnapshot = toSnapshot(state);

function setState(patch: Partial<SessionState>): void {
  state = { ...state, ...patch };
  // La instantánea se recalcula AQUÍ, no en `getSnapshot`: ver el comentario de `getSnapshot`.
  snapshot = toSnapshot(state);
  for (const listener of listeners) listener();
}

/** Exportadas para `session.test.ts`: la estabilidad de la referencia de `getSnapshot` es un
 * invariante de `useSyncExternalStore` que solo se puede comprobar desde fuera del componente. */
export function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function toSnapshot(source: SessionState): SessionSnapshot {
  return { status: source.status, operator: source.operator };
}

/**
 * `useSyncExternalStore` compara la instantánea por REFERENCIA para decidir si hay que volver a
 * renderizar. Devolver un objeto nuevo en cada llamada —como hacía esto— le hace creer que el
 * estado cambia en cada render, y entra en un bucle infinito: React lo corta con el error #185
 * («Maximum update depth exceeded») y la aplicación no pinta nada. La instantánea se cachea y solo
 * se sustituye cuando el estado cambia de verdad, en `setState`.
 */
export function getSnapshot(): SessionSnapshot {
  return snapshot;
}

function readStoredRefreshToken(): string | null {
  try {
    return sessionStorage.getItem(REFRESH_STORAGE_KEY);
  } catch {
    return null;
  }
}

function storeRefreshToken(token: string | null): void {
  try {
    if (token) sessionStorage.setItem(REFRESH_STORAGE_KEY, token);
    else sessionStorage.removeItem(REFRESH_STORAGE_KEY);
  } catch {
    // sessionStorage no disponible (navegación privada, cuotas, etc.): la sesión no sobrevive a
    // una recarga, pero el login en curso sigue funcionando.
  }
}

let refreshTimer: ReturnType<typeof setTimeout> | null = null;

function clearRefreshTimer(): void {
  if (refreshTimer !== null) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }
}

function scheduleProactiveRefresh(expiresInSeconds: number): void {
  clearRefreshTimer();
  const delayMs = Math.max(0, expiresInSeconds * 1000 - PROACTIVE_REFRESH_SKEW_MS);
  refreshTimer = setTimeout(() => {
    refreshSession().catch(() => {
      // `refreshSession` ya deja la sesión en `anonymous` ante cualquier fallo; nada más que hacer.
    });
  }, delayMs);
}

function applyLoginResponse(response: OperatorLoginResponse): void {
  storeRefreshToken(response.refresh_token);
  setState({
    status: "authenticated",
    accessToken: response.access_token,
    accessTokenExpiresAt: Date.now() + response.expires_in * 1000,
    operator: response.operator,
  });
  scheduleProactiveRefresh(response.expires_in);
}

function clearSession(): void {
  clearRefreshTimer();
  storeRefreshToken(null);
  setState({ status: "anonymous", accessToken: null, accessTokenExpiresAt: null, operator: null });
}

/** `POST /api/v1/operators/login`. Lanza `ConsoleApiError` con el mensaje en español del API. */
export async function login(input: OperatorLoginInput): Promise<void> {
  setState({ status: "authenticating" });
  try {
    const body: OperatorLoginInput = input.totp
      ? { email: input.email, password: input.password, totp: input.totp }
      : { email: input.email, password: input.password };
    const response = await rawRequest("/api/v1/operators/login", { method: "POST", body }, OperatorLoginResponseSchema);
    applyLoginResponse(response);
  } catch (err) {
    setState({ status: "anonymous", accessToken: null, accessTokenExpiresAt: null, operator: null });
    throw err;
  }
}

let refreshInFlight: Promise<string> | null = null;

/**
 * `POST /api/v1/operators/refresh`. Coalesce llamadas concurrentes en una sola petición (varias
 * peticiones autenticadas en paralelo no deben disparar refrescos duplicados).
 */
export function refreshSession(): Promise<string> {
  if (refreshInFlight) return refreshInFlight;

  const refreshToken = readStoredRefreshToken();
  if (!refreshToken) {
    clearSession();
    return Promise.reject(
      new ConsoleApiError({ code: "unauthenticated", message: "La sesión expiró. Vuelve a iniciar sesión.", status: 401 }),
    );
  }

  refreshInFlight = (async () => {
    try {
      const response = await rawRequest(
        "/api/v1/operators/refresh",
        { method: "POST", body: { refresh_token: refreshToken } },
        OperatorLoginResponseSchema,
      );
      applyLoginResponse(response);
      return response.access_token;
    } catch (err) {
      clearSession();
      throw err;
    } finally {
      refreshInFlight = null;
    }
  })();
  return refreshInFlight;
}

/** `POST /api/v1/operators/logout`. Limpia la sesión local aunque la llamada al servidor falle. */
export async function logout(): Promise<void> {
  const refreshToken = readStoredRefreshToken();
  clearSession();
  if (!refreshToken) return;
  try {
    await rawRequest("/api/v1/operators/logout", { method: "POST", body: { refresh_token: refreshToken } }, z.void());
  } catch {
    // Best-effort: si el servidor no pudo revocar el refresh token, la sesión local ya se limpió
    // y el token dejó de usarse desde este navegador.
  }
}

/** Al montar la consola: si hay un refresh token guardado, intenta recuperar la sesión en silencio. */
export function restoreSession(): void {
  if (state.status !== "anonymous") return;
  const refreshToken = readStoredRefreshToken();
  if (!refreshToken) return;
  setState({ status: "restoring" });
  refreshSession().catch(() => {
    // `refreshSession` ya deja la sesión en `anonymous` ante cualquier fallo.
  });
}

function isAccessTokenStale(): boolean {
  return state.accessTokenExpiresAt === null || Date.now() >= state.accessTokenExpiresAt - PROACTIVE_REFRESH_SKEW_MS;
}

export type AuthorizedRequestInit = Omit<ApiRequestInit, "headers">;

/**
 * Llama a un endpoint autenticado del API de plataforma: adjunta el access token vigente
 * (refrescándolo antes si ya está por vencer) y reintenta una única vez ante un `401`
 * (docs/FASE-1.md §6: "Refresco proactivo 60 s antes de expirar y reintento único ante 401").
 */
export async function authorizedRequest<T>(path: string, init: AuthorizedRequestInit, schema: z.ZodType<T>): Promise<T> {
  let token = state.accessToken;
  if (!token || isAccessTokenStale()) {
    token = await refreshSession();
  }

  try {
    return await rawRequest(path, { ...init, headers: { authorization: `Bearer ${token}` } }, schema);
  } catch (err) {
    if (err instanceof ConsoleApiError && err.status === 401) {
      const freshToken = await refreshSession();
      return await rawRequest(path, { ...init, headers: { authorization: `Bearer ${freshToken}` } }, schema);
    }
    throw err;
  }
}

// --- Context + hook (docs/FASE-1.md §6: "estado de sesión (Context + hook useSession)") ---

const SessionContext = createContext<SessionSnapshot | null>(null);

export function SessionProvider({ children }: { children: ReactNode }): ReactElement {
  const snapshot = useSyncExternalStore(subscribe, getSnapshot);
  useEffect(() => {
    restoreSession();
  }, []);
  return createElement(SessionContext.Provider, { value: snapshot }, children);
}

export function useSession(): SessionSnapshot {
  const snapshot = useContext(SessionContext);
  if (!snapshot) {
    throw new Error("useSession debe usarse dentro de <SessionProvider>.");
  }
  return snapshot;
}

export { ConsoleApiError };
